import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { deckService } from '../services/deckService';
import { cardService, PaginatedCardsResponse } from '../services/cardService';
import { Deck, Card, CreateCardDto } from '../types';
import { t } from '../utils/translations';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import UpgradePrompt from '../components/UpgradePrompt';
import UpgradeOptionsModal from '../components/UpgradeOptionsModal';
import CardEditModal from '../components/CardEditModal';
import AddCardModal from '../components/AddCardModal';
import ImageLightbox from '../components/ImageLightbox';
import StudyConfigModal from '../components/StudyConfigModal';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { ArrowLeftIcon, PhotoIcon, XMarkIcon, PencilIcon, TrashIcon, GlobeAltIcon, LockClosedIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { CARD_LIMITS, getTextLength, validateCardContent } from '../utils/cardLimits';

const DeckDetail: React.FC = () => {
  const { id: deckId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { refreshSubscription, subscription, checkLimit } = useSubscription();
  
  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [cardStats, setCardStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [deletingDeck, setDeletingDeck] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [maturityFilter, setMaturityFilter] = useState<'all' | 'new' | 'upToDate' | 'due' | 'learning' | 'mature'>('all');
  
  // Enhanced filter states
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCardTags, setSelectedCardTags] = useState<string[]>([]);
  const [difficultyFilter, setDifficultyFilter] = useState<'all' | 'easy' | 'medium' | 'hard'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'difficulty' | 'reviews' | 'accuracy' | 'next'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCards, setTotalCards] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [cardsPerPage] = useState(20);
  const [loadingCards, setLoadingCards] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showDeckOptions, setShowDeckOptions] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [showEditDeckModal, setShowEditDeckModal] = useState(false);
  const [editDeckName, setEditDeckName] = useState('');
  const [editDeckDescription, setEditDeckDescription] = useState('');
  const [editDeckTags, setEditDeckTags] = useState<string[]>([]);
  const [savingDeck, setSavingDeck] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [showStudyConfigModal, setShowStudyConfigModal] = useState(false);
  
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  
  // Image upload states
  const [uploadingImageForCard, setUploadingImageForCard] = useState<string | null>(null);

  const formatInterval = (days: number) => {
    if (days < 30) {
      return `${days}d`;
    } else if (days < 365) {
      const months = (days / 30).toFixed(1);
      return `${months}m`;
    } else {
      const years = (days / 365).toFixed(1);
      return `${years}y`;
    }
  };

  const loadDeckData = useCallback(async () => {
    try {
      setLoading(true);
      const [deckData, statsData] = await Promise.all([
        deckService.getDeck(deckId!),
        cardService.getCardStats(deckId!)
      ]);
      
      setDeck(deckData);
      setCardStats(statsData);
      
      // Load first page of cards
      await loadCards(1);
    } catch (error) {
      console.error('Failed to load deck data:', error);
      toast.error(t('messages.failedToLoad'));
      navigate('/decks');
    } finally {
      setLoading(false);
    }
  }, [deckId, navigate]);

  const loadCards = async (page: number) => {
    if (!deckId) return;
    
    try {
      setLoadingCards(true);
      const response = await cardService.getCards(deckId, {
        page,
        limit: cardsPerPage,
        search: searchTerm || undefined,
        maturityFilter: maturityFilter !== 'all' ? maturityFilter : undefined,
        difficultyFilter: difficultyFilter !== 'all' ? difficultyFilter : undefined,
        tags: selectedCardTags.length > 0 ? selectedCardTags.join(',') : undefined,
        sortBy: sortBy,
        sortOrder: sortOrder
      });
      
      // Check if response is paginated
      if ('cards' in response) {
        const paginatedResponse = response as PaginatedCardsResponse;
        setCards(paginatedResponse.cards);
        setTotalCards(paginatedResponse.total);
        setTotalPages(paginatedResponse.totalPages);
        setCurrentPage(paginatedResponse.page);
      } else {
        // Backward compatibility: response is array of cards
        setCards(response as Card[]);
        setTotalCards((response as Card[]).length);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Failed to load cards:', error);
      toast.error('Falha ao carregar cards');
    } finally {
      setLoadingCards(false);
    }
  };

  useEffect(() => {
    if (deckId) {
      loadDeckData();
    }
  }, [deckId, loadDeckData]);

  // Reload cards when search or filter changes
  useEffect(() => {
    if (deckId && !loading) {
      setCurrentPage(1);
      loadCards(1);
    }
  }, [searchTerm, maturityFilter, difficultyFilter, selectedCardTags, sortBy, sortOrder]);

  // Apply client-side filtering and sorting (now handled by backend, so this just returns cards as-is)
  const getFilteredAndSortedCards = () => {
    // All filtering and sorting is now done on the backend
    return cards;
  };

  const displayCards = getFilteredAndSortedCards();

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadCards(newPage);
      // Scroll to top of cards list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleAddCardFromModal = async (cardData: CreateCardDto) => {
    if (!deckId) return;

    try {
      await cardService.createCard(deckId, cardData);
      toast.success(t('messages.cardAdded'));
      
      // Reload current page and refresh stats
      await Promise.all([
        loadCards(currentPage),
        cardService.getCardStats(deckId).then(stats => setCardStats(stats))
      ]);
    } catch (error: any) {
      console.error('Failed to add card:', error);
      
      // Handle card limit error with upgrade prompt
      if (error.response?.data?.code === 'CARD_LIMIT_REACHED') {
        setShowUpgradePrompt(true);
      } else {
        toast.error(t('messages.failedToCreate'));
      }
      throw error; // Re-throw to let modal handle the error state
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!window.confirm(t('messages.deleteConfirmation'))) return;

    try {
      await cardService.deleteCard(cardId);
      toast.success(t('messages.cardDeleted'));
      
      // Reload current page and refresh stats
      await Promise.all([
        loadCards(currentPage),
        deckId ? cardService.getCardStats(deckId).then(stats => setCardStats(stats)) : Promise.resolve()
      ]);
    } catch (error) {
      console.error('Failed to delete card:', error);
      toast.error(t('messages.failedToDelete'));
    }
  };

  const startEditing = (card: Card) => {
    setEditingCard(card);
    setIsEditModalOpen(true);
  };

  const handleModalSave = async (front: string, back: string, difficulty: number, tags: string[], frontImage?: string, backImage?: string) => {
    if (!editingCard) return;

    try {
      // Build the update object
      const updateData: any = {
        front,
        back,
        difficulty,
        tags,
      };
      
      // Only include backImage in the update
      updateData.backImage = backImage || null;
      
      const updatedCard = await cardService.updateCard(editingCard._id, updateData);
      setCards(cards.map(card => card._id === updatedCard._id ? updatedCard : card));
      setEditingCard(null);
      setIsEditModalOpen(false);
      toast.success(t('messages.cardUpdated'));
    } catch (error) {
      console.error('Failed to update card:', error);
      toast.error(t('messages.failedToUpdate'));
      throw error; // Re-throw to let modal handle the error state
    }
  };

  const handleBackImageUpload = async (cardId: string, file: File) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato de imagem inválido. Use JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (2MB limit)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast.error('Imagem muito grande. O tamanho máximo é 2MB.');
      return;
    }

    setUploadingImageForCard(cardId);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        
        try {
          // Update the card with the new image
          const card = cards.find(c => c._id === cardId);
          if (!card) return;
          
          const updatedCard = await cardService.updateCard(cardId, {
            front: card.front,
            back: card.back,
            difficulty: card.difficulty,
            tags: card.tags || [],
            backImage: base64String,
          });
          
          setCards(cards.map(c => c._id === updatedCard._id ? updatedCard : c));
          setUploadingImageForCard(null);
          toast.success('Imagem carregada com sucesso!');
        } catch (error) {
          console.error('Failed to update card with image:', error);
          toast.error('Erro ao salvar a imagem.');
          setUploadingImageForCard(null);
        }
      };
      
      reader.onerror = () => {
        toast.error('Erro ao carregar a imagem.');
        setUploadingImageForCard(null);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Erro ao carregar a imagem.');
      setUploadingImageForCard(null);
    }
  };

  const handleRemoveBackImage = async (cardId: string) => {
    try {
      const card = cards.find(c => c._id === cardId);
      if (!card) return;
      
      const updatedCard = await cardService.updateCard(cardId, {
        front: card.front,
        back: card.back,
        difficulty: card.difficulty,
        tags: card.tags || [],
        backImage: '',
      });
      
      setCards(cards.map(c => c._id === updatedCard._id ? updatedCard : c));
      toast.success('Imagem removida com sucesso!');
    } catch (error) {
      console.error('Failed to remove image:', error);
      toast.error('Erro ao remover a imagem.');
    }
  };

  const cancelEditing = () => {
    setEditingCard(null);
  };

  const handleEditDeck = () => {
    if (!deck) return;
    setEditDeckName(deck.name);
    setEditDeckDescription(deck.description || '');
    setEditDeckTags(deck.tags || []);
    setShowEditDeckModal(true);
  };

  const handleSaveDeck = async () => {
    if (!deckId || !editDeckName.trim()) {
      toast.error('O nome do deck não pode estar vazio');
      return;
    }

    try {
      setSavingDeck(true);
      const updatedDeck = await deckService.updateDeck(deckId, {
        name: editDeckName,
        description: editDeckDescription,
        tags: editDeckTags,
      });
      setDeck(updatedDeck);
      toast.success('Deck atualizado com sucesso!');
      setShowEditDeckModal(false);
    } catch (error) {
      console.error('Failed to update deck:', error);
      toast.error('Erro ao atualizar deck');
    } finally {
      setSavingDeck(false);
    }
  };

  const handleTogglePublic = async () => {
    if (!deckId || !deck) return;

    try {
      setTogglingPublic(true);
      const newPublicStatus = !deck.isPublic;
      const updatedDeck = await deckService.updateDeck(deckId, {
        isPublic: newPublicStatus,
      });
      setDeck(updatedDeck);
      toast.success(newPublicStatus ? 'Deck agora é público!' : 'Deck agora é privado!');
    } catch (error) {
      console.error('Failed to toggle public status:', error);
      toast.error('Erro ao alterar visibilidade do deck');
    } finally {
      setTogglingPublic(false);
    }
  };

  const handleDeleteDeck = async () => {
    if (!deckId) return;
    
    try {
      setDeletingDeck(true);
      await deckService.deleteDeck(deckId);
      toast.success(t('messages.flashcardDeleted'));
      // Refresh subscription to update deck count
      await refreshSubscription();
      navigate('/decks');
    } catch (error: any) {
      console.error('Failed to delete deck:', error);
      const message = error.response?.data?.message || t('messages.failedToDelete');
      toast.error(message);
    } finally {
      setDeletingDeck(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleRestartDeck = async () => {
    if (!deckId || !deck) return;
    
    const confirmed = window.confirm(
      'Tem certeza que deseja reiniciar o progresso deste deck? Todos os dados de progresso dos cards serão perdidos e você começará do zero.'
    );
    
    if (!confirmed) return;
    
    try {
      const result = await deckService.resetDeckProgress(deckId);
      toast.success(`Progresso reiniciado! ${result.modifiedCount} cards foram resetados.`);
      
      // Reload the deck data to reflect changes
      await loadDeckData();
    } catch (error) {
      console.error('Failed to restart deck:', error);
      toast.error('Falha ao reiniciar o progresso do deck. Tente novamente.');
    }
  };

  const handleExportToTxt = () => {
    if (!deck || !cards) return;

    // Helper function to strip HTML tags from rich text content
    const stripHtml = (html: string) => {
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.textContent || temp.innerText || '';
    };

    // Create the TXT content
    let content = `DECK: ${deck.name}\n`;
    content += `==========================================\n\n`;
    
    if (deck.description) {
      content += `Descrição: ${deck.description}\n\n`;
    }
    
    content += `Total de Cards: ${cards.length}\n`;
    content += `Criado em: ${new Date(deck.createdAt).toLocaleDateString('pt-BR')}\n\n`;
    content += `==========================================\n\n`;

    // Add each card
    cards.forEach((card, index) => {
      content += `CARD ${index + 1}\n`;
      content += `----------\n`;
      content += `Pergunta: ${stripHtml(card.front)}\n\n`;
      content += `Resposta: ${stripHtml(card.back)}\n\n`;
      
      content += `Dificuldade: ${card.difficulty}/5\n`;
      content += `==========================================\n\n`;
    });

    // Create and download the file
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Create a safe filename
    const safeFileName = deck.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    link.download = `${safeFileName}_cards.txt`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Deck exportado com sucesso!');
  };

  const handleImportFromTxt = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !deckId) return;

    // Reset the input value so the same file can be selected again
    event.target.value = '';

    if (!file.name.toLowerCase().endsWith('.txt')) {
      toast.error('Por favor, selecione um arquivo TXT');
      return;
    }

    try {
      setImporting(true);
      
      const text = await file.text();
      console.log('File content (first 1000 chars):', text.substring(0, 1000));
      console.log('File content (lines):', text.split('\n').slice(0, 20));
      
      const parsedCards = parseTxtContent(text);
      console.log('Parsed cards count:', parsedCards.length);
      console.log('Parsed cards:', parsedCards);
      
      if (parsedCards.length === 0) {
        console.log('Debug: No cards found, checking parsing...');
        // Let's try a simple test case
        const testContent = `DECK: Teste
==========================================

Total de Cards: 1
Criado em: 01/01/2024

==========================================

CARD 1
----------
Pergunta: Quanto é 2+2?

Resposta: 4

Dificuldade: 1/5
==========================================`;
        console.log('Test parsing result:', parseTxtContent(testContent));
        toast.error('Nenhum card válido encontrado no arquivo. Verifique o console para informações de debug.');
        return;
      }

      // Check card limits for free users
      if (subscription && subscription.subscriptionTier === 'free') {
        const maxCards = subscription.limits.maxCardsPerDeck || 100;
        const totalCards = cards.length + parsedCards.length;
        
        if (totalCards > maxCards) {
          toast.error(`Não é possível importar ${parsedCards.length} cards. Isso excederia seu limite de ${maxCards} cards por deck.`);
          return;
        }
      }

      // Check for duplicate cards
      const duplicateCards: CreateCardDto[] = [];
      const uniqueCards: CreateCardDto[] = [];
      
      for (const cardData of parsedCards) {
        const isDuplicate = cards.some(existingCard => 
          existingCard.front.trim().toLowerCase() === cardData.front.trim().toLowerCase() &&
          existingCard.back.trim().toLowerCase() === cardData.back.trim().toLowerCase()
        );
        
        if (isDuplicate) {
          duplicateCards.push(cardData);
        } else {
          uniqueCards.push(cardData);
        }
      }

      let cardsToImport = uniqueCards;

      // If duplicates found, ask user what to do
      if (duplicateCards.length > 0) {
        const shouldImportDuplicates = window.confirm(
          `${duplicateCards.length} card${duplicateCards.length > 1 ? 's' : ''} já exist${duplicateCards.length > 1 ? 'em' : 'e'} neste deck.\n\n` +
          `Cards únicos para importar: ${uniqueCards.length}\n` +
          `Cards duplicados: ${duplicateCards.length}\n\n` +
          `Deseja importar apenas os cards únicos? (OK para importar apenas únicos, Cancelar para importar todos)`
        );
        
        if (!shouldImportDuplicates) {
          cardsToImport = parsedCards; // Import all cards including duplicates
        }
      }

      // Import the cards
      const importedCards: Card[] = [];
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = duplicateCards.length > 0 && cardsToImport === uniqueCards ? duplicateCards.length : 0;

      for (const cardData of cardsToImport) {
        try {
          const newCard = await cardService.createCard(deckId, cardData);
          importedCards.push(newCard);
          successCount++;
        } catch (error) {
          console.error('Failed to import card:', error);
          errorCount++;
        }
      }

      // Update the local state
      setCards([...cards, ...importedCards]);
      
      // Refresh stats
      const statsData = await cardService.getCardStats(deckId);
      setCardStats(statsData);

      if (successCount > 0) {
        let message = `${successCount} card${successCount > 1 ? 's' : ''} importado${successCount > 1 ? 's' : ''} com sucesso`;
        
        if (skippedCount > 0) {
          message += `, ${skippedCount} duplicado${skippedCount > 1 ? 's' : ''} ignorado${skippedCount > 1 ? 's' : ''}`;
        }
        
        if (errorCount > 0) {
          message += `, ${errorCount} falharam`;
        }
        
        toast.success(message + '!');
      } else if (skippedCount > 0 && errorCount === 0) {
        toast.success(`Todos os ${skippedCount} cards já existem neste deck. Nenhum card foi importado.`);
      } else {
        toast.error('Falha ao importar cards');
      }

    } catch (error) {
      console.error('Failed to import cards:', error);
      toast.error('Falha ao ler o arquivo. Verifique o formato.');
    } finally {
      setImporting(false);
    }
  };

  const parseTxtContent = (content: string): CreateCardDto[] => {
    const cards: CreateCardDto[] = [];
    const lines = content.split('\n');
    
    let currentCard: Partial<CreateCardDto> = {};
    let section: 'none' | 'question' | 'answer' = 'none';
    let questionLines: string[] = [];
    let answerLines: string[] = [];

    // Helper function to save current card
    const saveCurrentCard = () => {
      if (questionLines.length > 0 && answerLines.length > 0) {
        currentCard.front = questionLines.join('\n').trim();
        currentCard.back = answerLines.join('\n').trim();
        
        if (currentCard.front && currentCard.back) {
          cards.push({
            front: currentCard.front,
            back: currentCard.back,
            difficulty: currentCard.difficulty || 1
          });
        }
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip deck header and stats (both PT and EN)
      if (line.startsWith('DECK:') || 
          line.startsWith('Description:') || line.startsWith('Descrição:') ||
          line.startsWith('Total Cards:') || line.startsWith('Total de Cards:') ||
          line.startsWith('Created:') || line.startsWith('Criado em:') ||
          line === '==========================================' || line === '') {
        continue;
      }

      // Start of a new card
      if (line.match(/^CARD \d+$/)) {
        // Save previous card if it exists
        saveCurrentCard();
        
        // Reset for new card
        currentCard = {};
        section = 'none';
        questionLines = [];
        answerLines = [];
        continue;
      }

      // Card separators
      if (line === '----------') {
        continue;
      }

      // Question section (both PT and EN)
      if (line.startsWith('Question:') || line.startsWith('Pergunta:')) {
        // If we were in answer section, save the answer
        if (section === 'answer' && answerLines.length > 0) {
          currentCard.back = answerLines.join('\n').trim();
        }
        
        section = 'question';
        questionLines = [];
        const prefix = line.startsWith('Question:') ? 'Question:' : 'Pergunta:';
        const questionText = line.substring(prefix.length).trim();
        if (questionText) {
          questionLines.push(questionText);
        }
        continue;
      }

      // Answer section (both PT and EN)
      if (line.startsWith('Answer:') || line.startsWith('Resposta:')) {
        // If we were in question section, save the question
        if (section === 'question' && questionLines.length > 0) {
          currentCard.front = questionLines.join('\n').trim();
        }
        
        section = 'answer';
        answerLines = [];
        const prefix = line.startsWith('Answer:') ? 'Answer:' : 'Resposta:';
        const answerText = line.substring(prefix.length).trim();
        if (answerText) {
          answerLines.push(answerText);
        }
        continue;
      }

      // Difficulty section (both PT and EN)
      if (line.startsWith('Difficulty:') || line.startsWith('Dificuldade:')) {
        // If we were in answer section, save the answer
        if (section === 'answer' && answerLines.length > 0) {
          currentCard.back = answerLines.join('\n').trim();
        }
        
        section = 'none';
        const difficultyMatch = line.match(/(Difficulty|Dificuldade):\s*(\d+)/);
        if (difficultyMatch) {
          currentCard.difficulty = parseInt(difficultyMatch[2], 10);
        }
        continue;
      }

      // Content lines for question or answer
      if (section === 'question' && line) {
        questionLines.push(line);
      } else if (section === 'answer' && line) {
        answerLines.push(line);
      }
    }

    // Don't forget the last card
    saveCurrentCard();

    // Debug logging
    console.log('Parsed cards:', cards);

    return cards;
  };

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline']
    ]
  };

  const quillFormats = [
    'bold', 'italic', 'underline'
  ];

  // Calculate card maturity based on review count and interval
  const getCardMaturity = (card: Card) => {
    if (card.reviewCount === 0) {
      return {
        level: 'new',
        label: 'Novo',
        color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200',
        dotColor: 'bg-blue-500'
      };
    } else if (card.interval < 21) {
      return {
        level: 'learning',
        label: 'Aprendendo',
        color: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200',
        dotColor: 'bg-orange-500'
      };
    } else if (card.interval >= 21) {
      return {
        level: 'mature',
        label: 'Maduro',
        color: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200',
        dotColor: 'bg-green-500'
      };
    } else {
      return {
        level: 'review',
        label: 'Revisão',
        color: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200',
        dotColor: 'bg-purple-500'
      };
    }
  };

  // Custom styles for ReactQuill in dark mode and mobile optimization
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      /* Touch targets */
      .touch-target {
        min-height: 44px;
        min-width: 44px;
      }
      
      /* Mobile editor optimization with better content sizing */
      .mobile-editor .ql-container {
        min-height: 120px !important;
        max-height: 400px !important;
        overflow-y: auto;
      }
      
      .mobile-editor .ql-editor {
        min-height: 100px !important;
        max-height: 380px !important;
        overflow-y: auto;
        padding: 12px;
        line-height: 1.6;
      }
      
      @media (max-width: 640px) {
        .mobile-editor .ql-toolbar {
          border-bottom: 1px solid #e5e7eb;
          border-radius: 0.5rem 0.5rem 0 0;
          padding: 8px;
        }
        
        .mobile-editor .ql-container {
          border-radius: 0 0 0.5rem 0.5rem;
          min-height: 140px !important;
          max-height: 320px !important;
        }
        
        .mobile-editor .ql-editor {
          padding: 12px;
          font-size: 16px; /* Prevents zoom on iOS */
          line-height: 1.6;
          min-height: 120px !important;
          max-height: 300px !important;
        }
        
        .mobile-editor .ql-toolbar .ql-formats {
          margin-right: 8px;
        }
      }
      
      /* Auto-expand for content */
      .mobile-editor .ql-editor:focus {
        min-height: 120px !important;
      }
      
      @media (min-width: 641px) {
        .mobile-editor .ql-editor:focus {
          min-height: 140px !important;
        }
      }
      
      /* Dark mode styles */
      .ql-dark .ql-editor {
        background-color: #374151 !important;
        color: #f3f4f6 !important;
        border-color: #4b5563 !important;
        min-height: 100px !important;
        max-height: 380px !important;
      }
      .ql-dark .ql-container {
        min-height: 120px !important;
        max-height: 400px !important;
      }
      .ql-dark .ql-toolbar {
        background-color: #4b5563 !important;
        border-color: #6b7280 !important;
      }
      .ql-dark .ql-toolbar .ql-stroke {
        stroke: #d1d5db !important;
      }
      .ql-dark .ql-toolbar .ql-fill {
        fill: #d1d5db !important;
      }
      .ql-dark .ql-toolbar .ql-picker-label {
        color: #d1d5db !important;
      }
      .ql-dark .ql-tooltip {
        background-color: #374151 !important;
        border-color: #4b5563 !important;
        color: #f3f4f6 !important;
      }
      .ql-dark .ql-tooltip input {
        background-color: #4b5563 !important;
        color: #f3f4f6 !important;
        border-color: #6b7280 !important;
      }
      
      /* Mobile dark mode adjustments */
      @media (max-width: 640px) {
        .ql-dark .ql-editor {
          min-height: 120px !important;
          max-height: 300px !important;
        }
        .ql-dark .ql-container {
          min-height: 140px !important;
          max-height: 320px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!deck) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('errors.deckNotFound')}</h2>
        <Link
          to="/decks"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700"
        >
          {t('decks.backToDecks')}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900/20 transition-all duration-500 px-px md:p-6">
      {/* Header */}
      <div className="mb-6">
        {/* Mobile-style back button and title */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/decks')}
                className="inline-flex items-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 mr-3"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight">{deck.name}</h1>
            </div>
            
            {/* Card count info - shown on desktop */}
            <div className="hidden sm:flex items-center space-x-2">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className={subscription && subscription.subscriptionTier === 'free' && totalCards >= (subscription.limits.maxCardsPerDeck || 100) ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                  {totalCards} {totalCards === 1 ? t('cards.card') : t('cards.cards')}
                </span>
                {subscription && subscription.subscriptionTier === 'free' && (
                  <span className="ml-1 text-gray-400">
                    / {subscription.limits.maxCardsPerDeck || 100}
                  </span>
                )}
              </div>
              
              {/* Edit and Delete buttons */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleTogglePublic}
                  disabled={togglingPublic}
                  className={`p-2 rounded-lg transition-all ${togglingPublic ? 'opacity-50 cursor-not-allowed' : ''} ${
                    deck.isPublic 
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                  title={deck.isPublic ? 'Deck Público - Clique para tornar privado' : 'Deck Privado - Clique para tornar público'}
                >
                  {deck.isPublic ? (
                    <GlobeAltIcon className="h-5 w-5" />
                  ) : (
                    <LockClosedIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={handleEditDeck}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                  title="Editar Deck"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                  title="Deletar Flashcard"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Card count info below title - shown on mobile */}
          <div className="sm:hidden ml-14 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span className={subscription && subscription.subscriptionTier === 'free' && totalCards >= (subscription.limits.maxCardsPerDeck || 100) ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                  {totalCards} {totalCards === 1 ? t('cards.card') : t('cards.cards')}
                </span>
                {subscription && subscription.subscriptionTier === 'free' && (
                  <span className="ml-1 text-gray-400">
                    / {subscription.limits.maxCardsPerDeck || 100}
                  </span>
                )}
              </div>
              
              {/* Edit and Delete buttons - mobile */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleTogglePublic}
                  disabled={togglingPublic}
                  className={`p-2 rounded-lg transition-all ${togglingPublic ? 'opacity-50 cursor-not-allowed' : ''} ${
                    deck.isPublic 
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                  title={deck.isPublic ? 'Deck Público - Clique para tornar privado' : 'Deck Privado - Clique para tornar público'}
                >
                  {deck.isPublic ? (
                    <GlobeAltIcon className="h-5 w-5" />
                  ) : (
                    <LockClosedIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={handleEditDeck}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                  title="Editar Deck"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                  title="Deletar Flashcard"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Card limit banner for free users */}
          {subscription && subscription.subscriptionTier === 'free' && cards.length >= (subscription.limits.maxCardsPerDeck || 100) * 0.9 && (
            <div className={`mb-4 rounded-lg p-3 border ${
              cards.length >= (subscription.limits.maxCardsPerDeck || 100)
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className={`w-5 h-5 mr-2 ${
                    cards.length >= (subscription.limits.maxCardsPerDeck || 100) ? 'text-red-400' : 'text-amber-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className={`text-sm ${
                    cards.length >= (subscription.limits.maxCardsPerDeck || 100) 
                      ? 'text-red-700 dark:text-red-300' 
                      : 'text-amber-700 dark:text-amber-300'
                  }`}>
                    {cards.length >= (subscription.limits.maxCardsPerDeck || 100) 
                      ? `${t('cards.cardLimitReachedMessage').replace('{limit}', String(subscription.limits.maxCardsPerDeck || 100))}`
                      : `${t('cards.approachingLimitMessage').replace('{remaining}', String((subscription.limits.maxCardsPerDeck || 100) - cards.length))}`
                    }
                  </p>
                </div>
                <button
                  onClick={() => setShowUpgradePrompt(true)}
                  className={`text-sm font-medium underline hover:no-underline ml-2 ${
                    cards.length >= (subscription.limits.maxCardsPerDeck || 100)
                      ? 'text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-300'
                      : 'text-amber-600 dark:text-amber-400 hover:text-amber-500 dark:hover:text-amber-300'
                  }`}
                >
                  {t('cards.upgrade')}
                </button>
              </div>
            </div>
          )}
          
          {deck.description && (
            <p className="text-gray-700 dark:text-gray-300 mt-4 text-sm sm:text-base bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl p-4 shadow-lg">{deck.description}</p>
          )}
        </div>

        {/* Action buttons - Mobile first layout */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 mt-4 mb-4">
          
          <div className="flex flex-row gap-3 sm:gap-2 flex-1">
            <button
              onClick={() => {
                // Check card limit before allowing to add
                if (subscription && subscription.subscriptionTier === 'free' && 
                    !checkLimit('card', cards.length)) {
                  setShowUpgradePrompt(true);
                  return;
                }
                
                setIsAddCardModalOpen(true);
              }}
              disabled={!!(subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cards.length))}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg touch-target hover:scale-105 ${
                subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cards.length)
                  ? 'text-gray-400 bg-gray-200 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
                  : 'text-white bg-gradient-to-r from-indigo-500 via-blue-600 to-indigo-600 hover:from-indigo-600 hover:via-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-900 shadow-indigo-500/25 hover:shadow-indigo-600/30 backdrop-blur-sm'
              }`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cards.length) 
                ? t('cards.cardLimitReached') 
                : t('cards.addCard')
              }
            </button>
            
            {/* Study Deck Button */}
            {cards.length > 0 && (
              <Link
                to={`/study/${deckId}`}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg touch-target text-white bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 hover:from-emerald-600 hover:via-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-900 hover:scale-105 shadow-emerald-500/25 hover:shadow-emerald-600/30 backdrop-blur-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                Estudar Deck
              </Link>
            )}
            
            {/* Dropdown menu for deck options */}
            <div className="relative">
              <button
                onClick={() => setShowDeckOptions(!showDeckOptions)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-gray-200/50 dark:border-gray-600/50 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-200 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl touch-target backdrop-blur-sm hover:scale-105"
                title="Mostrar opções do deck"
              >
                <Cog6ToothIcon className="w-5 h-5" />
              </button>
              
              {/* Dropdown menu */}
              {showDeckOptions && (
                <>
                  {/* Backdrop to close dropdown when clicking outside */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowDeckOptions(false)}
                  ></div>
                  
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 z-20 overflow-hidden">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowStatisticsModal(true);
                          setShowDeckOptions(false);
                        }}
                        disabled={cards.length === 0}
                        className={`w-full flex items-center px-4 py-3 text-sm text-left transition-colors ${
                          cards.length === 0
                            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={cards.length === 0 ? 'Nenhum card para mostrar estatísticas' : 'Ver estatísticas detalhadas do deck'}
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Ver Estatísticas
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowStudyConfigModal(true);
                          setShowDeckOptions(false);
                        }}
                        disabled={cards.length === 0}
                        className={`w-full flex items-center px-4 py-3 text-sm text-left transition-colors ${
                          cards.length === 0
                            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={cards.length === 0 ? 'Nenhum card disponível' : 'Configurar quantidade de cards para estudar'}
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Configurar Estudo
                      </button>
                      
                      <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                      
                      <button
                        onClick={() => {
                          handleExportToTxt();
                          setShowDeckOptions(false);
                        }}
                        disabled={cards.length === 0}
                        className={`w-full flex items-center px-4 py-3 text-sm text-left transition-colors ${
                          cards.length === 0
                            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={cards.length === 0 ? 'Nenhum card para exportar' : 'Exportar deck para arquivo TXT'}
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Exportar TXT
                      </button>
                      
                      <label
                        className={`w-full flex items-center px-4 py-3 text-sm cursor-pointer transition-colors ${
                          importing
                            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title="Importar cards de arquivo TXT"
                      >
                        <input
                          type="file"
                          accept=".txt"
                          onChange={(e) => {
                            handleImportFromTxt(e);
                            setShowDeckOptions(false);
                          }}
                          disabled={importing}
                          className="hidden"
                        />
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        {importing ? 'Importando...' : 'Importar TXT'}
                      </label>
                      
                      <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                      
                      <button
                        onClick={() => {
                          handleRestartDeck();
                          setShowDeckOptions(false);
                        }}
                        disabled={cards.length === 0}
                        className={`w-full flex items-center px-4 py-3 text-sm text-left transition-colors ${
                          cards.length === 0
                            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                            : 'text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                        }`}
                        title={cards.length === 0 ? 'Nenhum card para reiniciar' : 'Reiniciar todo o progresso do deck'}
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reiniciar Progresso
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar - Mobile optimized */}
      <div className="mb-4 sm:mb-6 space-y-3">
        {/* Search Input with Filter Button */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Buscar cards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-10 py-3 sm:py-2 border border-gray-200/50 dark:border-gray-600/50 rounded-2xl leading-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 dark:focus:ring-indigo-400/50 dark:focus:border-indigo-400/50 transition-all duration-300 shadow-lg dark:shadow-xl hover:shadow-xl dark:hover:shadow-2xl text-base sm:text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center touch-target"
              >
                <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 sm:py-2 rounded-2xl transition-all shadow-lg hover:shadow-xl flex items-center gap-2 ${
              showFilters || maturityFilter !== 'all' || selectedCardTags.length > 0 || difficultyFilter !== 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-600/50'
            }`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="hidden sm:inline text-sm font-medium">Filtros</span>
          </button>
        </div>

        {/* Expandable Filters Panel */}
        {showFilters && (
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-600/50 shadow-xl p-4 space-y-4">
            {/* Maturity Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Maturidade
              </label>
              <select
                value={maturityFilter}
                onChange={(e) => setMaturityFilter(e.target.value as 'all' | 'new' | 'upToDate' | 'due' | 'learning' | 'mature')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">Todos os Cards</option>
                <option value="new">Novos</option>
                <option value="upToDate">Em dia</option>
                <option value="due">Para Revisar</option>
                <option value="learning">Aprendendo (1-20 dias)</option>
                <option value="mature">Maduros (≥21 dias)</option>
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Dificuldade
              </label>
              <select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value as 'all' | 'easy' | 'medium' | 'hard')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">Todas as Dificuldades</option>
                <option value="easy">Fácil (≤ 2.5)</option>
                <option value="medium">Médio (2.5 - 3.5)</option>
                <option value="hard">Difícil (≥ 3.5)</option>
              </select>
            </div>

            {/* Tags Filter */}
            {cards.length > 0 && (() => {
              const allCardTags = Array.from(new Set(cards.flatMap(card => card.tags || []))).sort();
              return allCardTags.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 dark:border-gray-600 rounded-lg">
                    {allCardTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => {
                          setSelectedCardTags(prev =>
                            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
                          );
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          selectedCardTags.includes(tag)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Sort Options */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ordenar por
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'date' | 'difficulty' | 'reviews' | 'accuracy' | 'next')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="date">Data</option>
                  <option value="difficulty">Dificuldade</option>
                  <option value="reviews">Revisões</option>
                  <option value="accuracy">Precisão</option>
                  <option value="next">Próximo</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ordem
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="desc">Decrescente</option>
                  <option value="asc">Crescente</option>
                </select>
              </div>
            </div>

            {/* Clear All Filters */}
            {(maturityFilter !== 'all' || selectedCardTags.length > 0 || difficultyFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setMaturityFilter('all');
                  setSelectedCardTags([]);
                  setDifficultyFilter('all');
                  setSortBy('date');
                  setSortOrder('desc');
                }}
                className="w-full px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                Limpar Todos os Filtros
              </button>
            )}
          </div>
        )}

        {/* Active Filters Display */}
        {(maturityFilter !== 'all' || selectedCardTags.length > 0 || difficultyFilter !== 'all') && (
          <div className="flex flex-wrap items-center gap-2 px-2">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Filtros ativos:</span>
            
            {maturityFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                {maturityFilter === 'new' ? 'Novos' : 
                 maturityFilter === 'upToDate' ? 'Em dia' : 
                 maturityFilter === 'due' ? 'Para Revisar' :
                 maturityFilter === 'learning' ? 'Aprendendo' : 'Maduros'}
                <button onClick={() => setMaturityFilter('all')} className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}

            {difficultyFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs font-medium">
                {difficultyFilter === 'easy' ? 'Fácil' : difficultyFilter === 'medium' ? 'Médio' : 'Difícil'}
                <button onClick={() => setDifficultyFilter('all')} className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}

            {selectedCardTags.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                {tag}
                <button onClick={() => setSelectedCardTags(prev => prev.filter(t => t !== tag))} className="hover:bg-green-200 dark:hover:bg-green-800 rounded-full p-0.5">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
        
        {/* Results Summary */}
        {(searchTerm || maturityFilter !== 'all' || selectedCardTags.length > 0 || difficultyFilter !== 'all') && (
          <p className="text-sm text-gray-600 dark:text-gray-400 px-1">
            Exibindo {displayCards.length} de {totalCards} card{totalCards !== 1 ? 's' : ''}
            {displayCards.length !== totalCards && ' (filtros aplicados)'}
          </p>
        )}
      </div>

      {/* Cards List - Mobile optimized */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/50 via-white/30 to-gray-50/50 dark:from-gray-700/30 dark:via-gray-800/30 dark:to-gray-700/30 rounded-t-2xl backdrop-blur-sm">
          <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
            Cards ({displayCards.length > 0 ? `${displayCards.length} exibidos` : '0'})
          </h3>
        </div>
        
        {loadingCards ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-8 sm:py-12 px-4">
            {cards.length === 0 ? (
              <>
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Ainda não há cards</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm sm:text-base">Comece adicionando seu primeiro flashcard a este deck.</p>
                <button
                  onClick={() => {
                    // Check card limit before allowing to add
                    if (subscription && subscription.subscriptionTier === 'free' && 
                        !checkLimit('card', cards.length)) {
                      setShowUpgradePrompt(true);
                      return;
                    }
                    
                    setIsAddCardModalOpen(true);
                  }}
                  disabled={!!(subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cards.length))}
                  className={`inline-flex items-center px-6 py-3 sm:py-2 border border-transparent text-sm font-semibold rounded-xl touch-target shadow-lg hover:scale-105 transition-all duration-200 ${
                    subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cards.length)
                      ? 'text-gray-400 bg-gray-200 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
                      : 'text-white bg-gradient-to-r from-indigo-500 via-blue-600 to-indigo-600 hover:from-indigo-600 hover:via-blue-700 hover:to-indigo-700 shadow-indigo-500/25 hover:shadow-indigo-600/30'
                  }`}
                >
                  {subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cards.length) 
                    ? t('cards.cardLimitReached') 
                    : t('cards.addFirstCard')
                  }
                </button>
              </>
            ) : (
              <>
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nenhum card encontrado</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm sm:text-base">
                  {searchTerm 
                    ? `Nenhum card corresponde à sua busca por "${searchTerm}".`
                    : 'Nenhum card corresponde aos filtros selecionados.'
                  } Tente ajustar seus filtros.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setMaturityFilter('all');
                    setSelectedCardTags([]);
                    setDifficultyFilter('all');
                  }}
                  className="inline-flex items-center px-4 py-3 sm:py-2 border border-gray-200/50 dark:border-gray-600/50 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 touch-target shadow-lg hover:shadow-xl backdrop-blur-sm hover:scale-105 transition-all duration-200"
                >
                  Limpar Filtros
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
              {displayCards.map((card) => (
              <div key={card._id} className="p-4 sm:p-6 hover:bg-gradient-to-r hover:from-blue-50/30 hover:via-indigo-50/20 hover:to-purple-50/30 dark:hover:from-blue-900/10 dark:hover:via-indigo-900/10 dark:hover:to-purple-900/10 transition-all duration-200 hover:shadow-lg">
                <div className="space-y-4">
                  {/* Card content - Stack on mobile, side by side on larger screens */}
                  <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
                    <div className="bg-gradient-to-br from-blue-50/80 via-white/60 to-indigo-50/80 dark:from-gray-700/50 dark:via-gray-800/40 dark:to-blue-900/20 rounded-xl p-3 sm:p-4 border border-blue-200/30 dark:border-gray-600/50 shadow-lg backdrop-blur-sm">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">Frente</h4>
                      <div className="text-gray-900 dark:text-white prose prose-sm dark:prose-invert max-w-none text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: card.front }} />
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50/80 via-white/60 to-green-50/80 dark:from-gray-700/50 dark:via-gray-800/40 dark:to-emerald-900/20 rounded-xl p-3 sm:p-4 border border-emerald-200/30 dark:border-gray-600/50 shadow-lg backdrop-blur-sm">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-emerald-900 dark:text-emerald-300">Verso</h4>
                        {!card.backImage && uploadingImageForCard !== card._id && (
                          <>
                            <input
                              type="file"
                              id={`back-image-upload-${card._id}`}
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleBackImageUpload(card._id, file);
                                }
                              }}
                            />
                            <button
                              onClick={() => document.getElementById(`back-image-upload-${card._id}`)?.click()}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 rounded-lg transition-colors duration-200"
                              title="Adicionar imagem"
                            >
                              <PhotoIcon className="h-4 w-4 mr-1" />
                              Imagem
                            </button>
                          </>
                        )}
                        {uploadingImageForCard === card._id && (
                          <span className="text-xs text-emerald-600 dark:text-emerald-400">
                            Carregando...
                          </span>
                        )}
                      </div>
                      <div className="flex gap-3 items-start">
                        <div className="flex-1 text-gray-900 dark:text-white prose prose-sm dark:prose-invert max-w-none text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: card.back }} />
                        {card.backImage && (
                          <div className="relative flex-shrink-0" style={{ maxWidth: '200px' }}>
                            <img 
                              src={card.backImage} 
                              alt="Back visual" 
                              className="w-full h-auto max-h-32 object-contain rounded-lg border border-emerald-200/50 dark:border-gray-600/50 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => {
                                setLightboxImage(card.backImage!);
                                setLightboxOpen(true);
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                              title="Clique para ver em tamanho real"
                            />
                            <button
                              onClick={() => handleRemoveBackImage(card._id)}
                              className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-colors"
                              title="Remover imagem"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Card metadata and actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${getCardMaturity(card).color}`}>
                        <div className={`w-2 h-2 rounded-full mr-1.5 ${getCardMaturity(card).dotColor}`}></div>
                        {getCardMaturity(card).label}
                      </span>
                      <span className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1 rounded-lg font-medium shadow-sm">Dificuldade: {card.difficulty}/5</span>
                      <span className="bg-gradient-to-r from-indigo-100 to-blue-200 dark:from-indigo-900/40 dark:to-blue-900/40 text-indigo-700 dark:text-indigo-300 px-2.5 py-1 rounded-lg font-medium shadow-sm">Revisões: {card.reviewCount}</span>
                      <span className="bg-gradient-to-r from-emerald-100 to-green-200 dark:from-emerald-900/40 dark:to-green-900/40 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 rounded-lg font-medium shadow-sm">
                        Precisão: {card.reviewCount > 0 ? Math.min(Math.round((card.correctCount / card.reviewCount) * 100), 100) : 0}%
                      </span>
                      {card.interval > 0 && (
                        <span className="bg-gradient-to-r from-purple-100 to-pink-200 dark:from-purple-900/40 dark:to-pink-900/40 text-purple-700 dark:text-purple-300 px-2.5 py-1 rounded-lg font-medium shadow-sm">
                          Próximo: {formatInterval(card.interval)}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 self-start sm:self-center">
                      <button
                        onClick={() => startEditing(card)}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl transition-all duration-200 shadow-lg hover:shadow-blue-500/25 touch-target hover:scale-105"
                      >
                        <svg className="w-4 h-4 mr-1 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCard(card._id)}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 rounded-xl transition-all duration-200 shadow-lg hover:shadow-red-500/25 touch-target hover:scale-105"
                      >
                        <svg className="w-4 h-4 mr-1 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/50 via-white/30 to-gray-50/50 dark:from-gray-700/30 dark:via-gray-800/30 dark:to-gray-700/30">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                {/* Page Info */}
                <div className="text-sm text-gray-600 dark:text-gray-400 order-2 sm:order-1">
                  Página {currentPage} de {totalPages}
                </div>
                
                {/* Pagination Buttons */}
                <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2">
                  {/* First Page - Hidden on mobile */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="hidden sm:inline-flex px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
                  >
                    Primeira
                  </button>
                  
                  {/* Previous */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 sm:px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
                  >
                    <span className="hidden sm:inline">Anterior</span>
                    <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(totalPages <= 768 ? 3 : 5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      const maxPages = window.innerWidth < 640 ? 3 : 5;
                      
                      if (totalPages <= maxPages) {
                        pageNum = i + 1;
                      } else if (currentPage <= Math.ceil(maxPages / 2)) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - Math.floor(maxPages / 2)) {
                        pageNum = totalPages - maxPages + 1 + i;
                      } else {
                        pageNum = currentPage - Math.floor(maxPages / 2) + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-2 sm:px-3 py-2 text-sm font-medium rounded-lg transition-colors min-w-[36px] sm:min-w-[40px] ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Next */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-2 sm:px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <svg className="w-5 h-5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  
                  {/* Last Page - Hidden on mobile */}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="hidden sm:inline-flex px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
                  >
                    Última
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
        )}
      </div>
      
      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <UpgradeOptionsModal
          message="Você atingiu o limite de cards para este deck no plano gratuito."
          feature="cards ilimitados"
          onClose={() => setShowUpgradePrompt(false)}
        />
      )}

      {/* Card Edit Modal */}
      <CardEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingCard(null);
        }}
        card={editingCard}
        onSave={handleModalSave}
      />

      {/* Add Card Modal */}
      <AddCardModal
        isOpen={isAddCardModalOpen}
        onClose={() => setIsAddCardModalOpen(false)}
        onSave={handleAddCardFromModal}
        subscription={subscription}
        cardCount={cards.length}
        checkLimit={checkLimit}
        onUpgradePrompt={() => setShowUpgradePrompt(true)}
      />

      {/* Statistics Modal */}
      {showStatisticsModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" 
            onClick={() => setShowStatisticsModal(false)}
          ></div>
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50 dark:border-gray-700/50">
                <h2 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Estatísticas do Deck
                </h2>
                <button
                  onClick={() => setShowStatisticsModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Fechar"
                >
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Statistics Content */}
              <div className="p-6 space-y-6">
                {cardStats ? (
                  <>
                    {/* Total Cards - Hero Card */}
                    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/30 dark:via-purple-900/30 dark:to-pink-900/30 rounded-2xl p-6 text-center border-2 border-indigo-200/50 dark:border-indigo-700/50 shadow-lg">
                      <div className="text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent mb-2">
                        {cardStats.totalCards}
                      </div>
                      <div className="text-base font-semibold text-gray-700 dark:text-gray-300">Total de Cards</div>
                    </div>

                    {/* Status Cards Grid */}
                    <div className="grid grid-cols-3 gap-3">
                      {/* New Cards */}
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/30 dark:to-blue-800/20 rounded-xl p-4 text-center border border-blue-200/60 dark:border-blue-700/50 hover:shadow-md transition-shadow">
                        <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1.5">
                          {cardStats.newCards}
                        </div>
                        <div className="text-xs font-semibold text-blue-700 dark:text-blue-300">Novos</div>
                      </div>
                      
                      {/* Up to Date Cards */}
                      <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/30 dark:to-green-800/20 rounded-xl p-4 text-center border border-green-200/60 dark:border-green-700/50 hover:shadow-md transition-shadow">
                        <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1.5">
                          {cardStats.upToDateCards}
                        </div>
                        <div className="text-xs font-semibold text-green-700 dark:text-green-300">Em dia</div>
                      </div>
                      
                      {/* Due for Review */}
                      <div className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/30 dark:to-red-800/20 rounded-xl p-4 text-center border border-red-200/60 dark:border-red-700/50 hover:shadow-md transition-shadow">
                        <div className="text-3xl font-bold text-red-600 dark:text-red-400 mb-1.5">
                          {cardStats.dueCards}
                        </div>
                        <div className="text-xs font-semibold text-red-700 dark:text-red-300">Para Revisar</div>
                      </div>
                    </div>

                    {/* Maturity Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                        <h3 className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Maturidade</h3>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Young Cards */}
                        <div className="bg-gradient-to-br from-amber-50 to-yellow-100/50 dark:from-amber-900/30 dark:to-yellow-800/20 rounded-xl p-5 text-center border border-amber-200/60 dark:border-amber-700/50 hover:shadow-md transition-shadow">
                          <div className="text-4xl font-bold text-amber-600 dark:text-amber-400 mb-2">
                            {cardStats.youngCards}
                          </div>
                          <div className="text-sm font-semibold text-amber-700 dark:text-amber-300 mb-1">Aprendendo</div>
                          <div className="inline-block px-2.5 py-1 rounded-full bg-amber-200/60 dark:bg-amber-800/40 text-xs font-medium text-amber-800 dark:text-amber-200">
                            1-20 dias
                          </div>
                        </div>

                        {/* Mature Cards */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-100/50 dark:from-emerald-900/30 dark:to-teal-800/20 rounded-xl p-5 text-center border border-emerald-200/60 dark:border-emerald-700/50 hover:shadow-md transition-shadow">
                          <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400 mb-2">
                            {cardStats.matureCards}
                          </div>
                          <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-1">Maduros</div>
                          <div className="inline-block px-2.5 py-1 rounded-full bg-emerald-200/60 dark:bg-emerald-800/40 text-xs font-medium text-emerald-800 dark:text-emerald-200">
                            ≥21 dias
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Total Reviews */}
                    <div className="bg-gradient-to-br from-violet-50 to-purple-100/50 dark:from-violet-900/30 dark:to-purple-800/20 rounded-xl p-5 text-center border border-violet-200/60 dark:border-violet-700/50 shadow-sm">
                      <div className="text-4xl font-bold text-violet-600 dark:text-violet-400 mb-2">
                        {cardStats.totalReviews}
                      </div>
                      <div className="text-sm font-semibold text-violet-700 dark:text-violet-300">Total de Revisões</div>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    Carregando estatísticas...
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200/50 dark:border-gray-700/50 text-center">
                <button
                  onClick={() => setShowStatisticsModal(false)}
                  className="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Deck Modal */}
      {showEditDeckModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-2xl shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Editar Deck
              </h3>
              <button
                onClick={() => setShowEditDeckModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome
                </label>
                <input
                  type="text"
                  value={editDeckName}
                  onChange={(e) => setEditDeckName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all"
                  placeholder="Nome do deck"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={editDeckDescription}
                  onChange={(e) => setEditDeckDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all resize-none"
                  placeholder="Descrição do deck (opcional)"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {editDeckTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                    >
                      {tag}
                      <button
                        onClick={() => setEditDeckTags(editDeckTags.filter((t) => t !== tag))}
                        className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="tag-input"
                    placeholder="Adicionar tag (pressione Enter)"
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = e.currentTarget;
                        const newTag = input.value.trim();
                        if (newTag && !editDeckTags.includes(newTag)) {
                          setEditDeckTags([...editDeckTags, newTag]);
                          input.value = '';
                        }
                      }
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Pressione Enter para adicionar uma tag
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowEditDeckModal(false)}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={savingDeck}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveDeck}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={savingDeck || !editDeckName.trim()}
              >
                {savingDeck ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">
                Deletar Flashcard?
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tem certeza que deseja deletar este flashcard? Todos os cards serão perdidos. Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                  disabled={deletingDeck}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteDeck}
                  className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={deletingDeck}
                >
                  {deletingDeck ? 'Deletando...' : 'Deletar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <StudyConfigModal
        isOpen={showStudyConfigModal}
        onClose={() => setShowStudyConfigModal(false)}
        onStartStudy={(cardLimit) => {
          navigate(`/study/${deckId}`, { state: { cardLimit } });
        }}
        deckName={deck?.name || ''}
        availableCards={cardStats?.dueCards || totalCards}
        deckId={deckId || ''}
      />

      <ImageLightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImage}
        onClose={() => setLightboxOpen(false)}
        alt="Card Image"
      />
    </div>
  );
};

export default DeckDetail;
